import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { SignupSurveyCardPayload, SignupSurveyEligibilityData } from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  getCachedSignupSurveyCard,
  isEligibilityChecked,
  isSignupSurveySubmitted,
  markEligibilityChecked,
  markSignupSurveySubmitted,
  setCachedSignupSurveyCard,
  type CachedSignupSurveyCard,
} from '../lib/signupSurveyPrefs'
import { mapSignupSurveyToAvailableItem } from '../lib/mapSignupSurveyCard'
import type { AvailableSurveyApiItem } from '@/features/home/api/available-surveys-queries'

function invalidateSignupSurveyNotificationQueries(queryClient: QueryClient): void {
  Promise.resolve(
    queryClient.invalidateQueries({ queryKey: queryKeys.spNotifications.unreadCount() }),
  ).catch(() => undefined)
  Promise.resolve(queryClient.invalidateQueries({ queryKey: queryKeys.spNotifications.all() })).catch(
    () => undefined,
  )
}

function toCachedCard(card: SignupSurveyCardPayload): CachedSignupSurveyCard {
  return {
    instrumentId: card.instrumentId,
    campaignId: card.campaignId,
    name: card.name,
    description: card.description ?? null,
    estimatedCompletionMinutes: card.estimatedCompletionMinutes,
    questionCount: card.questionCount,
    reward: {
      enabled: card.reward.enabled,
      amount: card.reward.amount,
      display: card.reward.display,
    },
    availableSlots: card.availableSlots,
    instrumentStatus: card.instrumentStatus,
    lastAnsweredQuestionId: card.lastAnsweredQuestionId,
    hasActiveSession: card.hasActiveSession,
    activeResponseId: card.activeResponseId,
  }
}

async function fetchSignupSurveyEligibility(): Promise<SignupSurveyEligibilityData> {
  const idempotencyKey = crypto.randomUUID()
  const { data } = await apiClient.post<ApiResponse<SignupSurveyEligibilityData>>(
    '/api/v1/sp/signup-survey/eligibility',
    {},
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data.data
}

/**
 * Drop the signup card from Home immediately after submit (TC-27).
 * Prefs alone are not enough — eligibility query uses staleTime: Infinity.
 */
export function clearSignupSurveyHomeCard(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
): void {
  markSignupSurveySubmitted(userId)
  queryClient.setQueryData(queryKeys.spSignupSurvey.eligibility(userId), null)
}

/**
 * One-shot eligibility check (BFP-1025). Skips the network call when already checked
 * for this user; still exposes the cached Home card when present.
 */
export function useSignupSurveyEligibility() {
  const userId = useSessionStore((s) => s.user?.id)
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: queryKeys.spSignupSurvey.eligibility(userId ?? 'anonymous'),
    enabled: Boolean(userId),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 503) return failureCount < 3
      return failureCount < 1
    },
    queryFn: async (): Promise<AvailableSurveyApiItem | null> => {
      if (!userId) return null

      if (isEligibilityChecked(userId)) {
        const cached = getCachedSignupSurveyCard(userId)
        if (cached) return mapSignupSurveyToAvailableItem(cached)
        // Completed signup → stay empty (TC-27).
        if (isSignupSurveySubmitted(userId)) return null
        // Checked-with-no-card can be a false empty from a first-login race —
        // re-query so new SPs still receive the signup survey.
      }

      const result = await fetchSignupSurveyEligibility()

      if (result.survey) {
        const cached = toCachedCard(result.survey)
        setCachedSignupSurveyCard(userId, cached)
        markEligibilityChecked(userId)
        if (result.notificationEnqueued) {
          invalidateSignupSurveyNotificationQueries(queryClient)
        }
        return mapSignupSurveyToAvailableItem(cached)
      }

      setCachedSignupSurveyCard(userId, null)
      markEligibilityChecked(userId)
      if (result.notificationEnqueued) {
        invalidateSignupSurveyNotificationQueries(queryClient)
      }
      return null
    },
  })
}
